/** Bookings — the bookings linked to the order, one row each linking to its booking page
 *  (the timeline bars are P4), then the start and end the order derives from them with
 *  the known gap: they are not recalculated when a booking's own end changes. Each
 *  booking's time is in its own tour's zone, labelled (an order may span tours in several
 *  zones); the order's derived start and end are UTC, labelled. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { OrderRecordStage } from '@/api/orderRecord';
import type { OrderBookingRow } from '@/api/orders';
import { BookingStateChip } from '@/features/bookings/BookingStateChip';
import { MONO_SX } from '@/features/bookings/record/panels/displays';
import { PanelMeta } from '@/features/bookings/record/panels/PanelMeta';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import type { ViewSection } from '@/features/records/views/types';

import { formatZoned } from '@/utils/zonedTime';

import { ORDER_TIMEZONE } from '../views/spineModel';

import type { OrderDisplayContext } from './displays';
import { OrderStagePanel } from './OrderStagePanel';

/** Rows shown before the list scrolls (plan §1.6: party rows scroll after 8 too). */
const VISIBLE_ROWS = 8;
const ROW_PX = 52;

function guestName(u: OrderBookingRow['user']): string | null {
  if (!u) return null;
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || `User #${u.id}`;
}

export function OrderBookingRows({ bookings }: { bookings: OrderBookingRow[] }) {
  if (bookings.length === 0) return <>No bookings are linked to this order</>;
  return (
    <Box
      component="ul"
      aria-label="Bookings of this order"
      data-order-bookings
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        maxHeight: VISIBLE_ROWS * ROW_PX,
        overflowY: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      {bookings.map((b, i) => {
        // The booking's own tour zone; UTC (labelled) when the tour names none.
        const when = b.start_ts
          ? formatZoned(b.start_ts, b.start_timezone ?? ORDER_TIMEZONE)
          : b.date
            ? `${b.date} (no start time)`
            : 'No date';
        const facts = [
          when,
          guestName(b.user),
          b.people_count != null ? `${b.people_count} ${b.people_count === 1 ? 'traveller' : 'travellers'}` : null,
        ].filter(Boolean);
        return (
          <Box component="li" key={b.id} sx={{ borderTop: i === 0 ? 0 : 1, borderColor: 'divider' }}>
            <ButtonBase
              component={RouterLink}
              to={`/bookings/${b.id}`}
              data-order-booking={b.id}
              sx={{
                width: '100%',
                minHeight: ROW_PX,
                px: 1.25,
                py: 0.75,
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                columnGap: 1.5,
                alignItems: 'center',
                textAlign: 'left',
                transition: 'background-color 150ms ease-out',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                '&:hover': { bgcolor: 'action.hover' },
                '&:hover .order-booking-tour': { textDecoration: 'underline', textUnderlineOffset: 3 },
                '&.Mui-focusVisible': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" component="span" sx={{ display: 'flex', gap: 1, alignItems: 'baseline', minWidth: 0 }}>
                  <Box component="span" sx={{ ...MONO_SX, color: 'text.secondary', flex: 'none' }}>
                    #{b.id}
                  </Box>
                  <Box
                    component="span"
                    className="order-booking-tour"
                    sx={{ fontWeight: 600, color: 'primary.main', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {b.tour?.name ?? (b.tour ? `Tour #${b.tour.id}` : 'No tour')}
                  </Box>
                </Typography>
                <Typography
                  variant="caption"
                  component="span"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}
                >
                  {facts.join(' · ')}
                </Typography>
              </Box>
              <BookingStateChip booking_state={b.booking_state} booking_state_display={b.booking_state_display} />
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
}

/** The derived start / end's provenance and their known gap, in the catalog's words. */
function DerivedNote() {
  const { fieldByKey } = useRecordEditor();
  const start = fieldByKey.get('start_datetime');
  const text = [start?.restech_note, start?.warning].filter(Boolean).join(' ');
  if (!text) return null;
  return (
    <Typography
      variant="caption"
      component="p"
      color="text.secondary"
      data-derived-note
      sx={{ m: 0, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
    >
      <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, flex: 'none' }} />
      <span>{text}</span>
    </Typography>
  );
}

export function BookingsPanel({
  section,
  display,
}: {
  section: ViewSection<OrderRecordStage>;
  display: OrderDisplayContext;
}) {
  const { bookings, booking_count: count } = display.detail;
  return (
    <OrderStagePanel
      section={section}
      display={display}
      meta={<PanelMeta>Order times in {ORDER_TIMEZONE}</PanelMeta>}
      systemTitle={{ title: 'Derived from the bookings', note: 'read-only' }}
      labels={{ bookings: `${count} linked ${count === 1 ? 'booking' : 'bookings'}` }}
      displays={{ bookings: <OrderBookingRows bookings={bookings} /> }}
      afterSystem={<DerivedNote />}
    />
  );
}
